// =============================================================================
// src/views/workspace/workspace-store.ts — Estado das análises (mockado, persistido).
//
// Fonte de verdade do Workspace: lista de WorkspaceAnalysis em memória, persistida
//   em localStorage com schema VERSIONADO ({version,analyses}). Em versão divergente
//   ou JSON inválido → reseed de WORKSPACE_SEED_ANALYSES. CRUD com undo de exclusão
//   e de reset. Relógio e storage são INJETADOS (DI) para testes determinísticos.
//
// SRP: só estado/persistência. Sem DOM, sem AppBus, sem tipos do app (testável puro).
// Proveniencia do padrão try/catch best-effort: collapse-controller.ts:65-98.
// =============================================================================

import type {
  WorkspaceAnalysis,
  WorkspaceCreateInput,
  WorkspacePersistedState,
} from './workspace-types';
import { WORKSPACE_SEED_ANALYSES } from './workspace-seed';
import { sectionLabelOf } from './workspace-sections';

/** Chave de persistência (best-effort, tolerante a localStorage indisponível). */
const WORKSPACE_STORAGE_KEY = 'workspaceAnalyses';
/** Versão do schema persistido; mismatch força reseed. */
const WORKSPACE_SCHEMA_VERSION = 1;
/** Abreviações de mês pt-BR para formatar a data de criação (UTC, determinístico). */
const MONTH_ABBR_PT = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
] as const;

/** Dependências injetadas: relógio e storage (subset de Storage). */
export interface WorkspaceStoreDeps {
  readonly now: () => number;
  readonly storage: Pick<Storage, 'getItem' | 'setItem'>;
}

/** Contrato público do store consumido pelo wiring do Workspace. */
export interface WorkspaceStore {
  listAnalyses(): readonly WorkspaceAnalysis[];
  getAnalysis(id: string): WorkspaceAnalysis | null;
  readonly selectedId: string | null;
  select(id: string): void;
  createAnalysis(input: WorkspaceCreateInput): WorkspaceAnalysis;
  deleteAnalysis(id: string): void;
  restoreAnalysis(): void;
  toggleFavorite(id: string): void;
  resetToSeed(): void;
  restoreReset(): void;
  subscribe(listener: () => void): () => void;
}

/** Type guard do payload persistido — barra null/array/número/versão errada. */
function isPersistedState(value: unknown): value is WorkspacePersistedState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { version?: unknown; analyses?: unknown };
  return candidate.version === WORKSPACE_SCHEMA_VERSION && Array.isArray(candidate.analyses);
}

/** Implementação do store; instância criada por createWorkspaceStore (DI). */
class WorkspaceAnalysisStore implements WorkspaceStore {
  private analyses: WorkspaceAnalysis[];
  private selected: string | null;
  private readonly listeners = new Set<() => void>();
  private lastDeleted: { analysis: WorkspaceAnalysis; index: number } | null = null;
  private preReset: { analyses: WorkspaceAnalysis[]; selected: string | null } | null = null;

  constructor(private readonly deps: WorkspaceStoreDeps) {
    this.analyses = this.loadOrSeed();
    /* v8 ignore next -- loadOrSeed nunca devolve lista vazia; ?? null é defensivo (noUncheckedIndexedAccess) */
    this.selected = this.analyses[0]?.id ?? null;
  }

  listAnalyses(): readonly WorkspaceAnalysis[] {
    return this.analyses;
  }

  getAnalysis(id: string): WorkspaceAnalysis | null {
    return this.analyses.find((analysis) => analysis.id === id) ?? null;
  }

  get selectedId(): string | null {
    return this.selected;
  }

  select(id: string): void {
    this.selected = id;
    this.notify();
  }

  createAnalysis(input: WorkspaceCreateInput): WorkspaceAnalysis {
    const analysis = this.buildAnalysis(input);
    this.analyses.unshift(analysis);
    this.selected = analysis.id;
    this.persistAndNotify();
    return analysis;
  }

  deleteAnalysis(id: string): void {
    const index = this.analyses.findIndex((analysis) => analysis.id === id);
    if (index < 0) return;
    const [removed] = this.analyses.splice(index, 1);
    /* v8 ignore next -- index validado por findIndex (>= 0); removed sempre definido */
    this.lastDeleted = removed ? { analysis: removed, index } : null;
    if (this.selected === id) this.selected = this.analyses[0]?.id ?? null;
    this.persistAndNotify();
  }

  restoreAnalysis(): void {
    if (!this.lastDeleted) return;
    const { analysis, index } = this.lastDeleted;
    this.analyses.splice(index, 0, analysis);
    this.selected = analysis.id;
    this.lastDeleted = null;
    this.persistAndNotify();
  }

  toggleFavorite(id: string): void {
    this.analyses = this.analyses.map((analysis) =>
      analysis.id === id ? { ...analysis, favorite: !analysis.favorite } : analysis,
    );
    this.persistAndNotify();
  }

  resetToSeed(): void {
    this.preReset = { analyses: this.analyses, selected: this.selected };
    this.analyses = [...WORKSPACE_SEED_ANALYSES];
    /* v8 ignore next -- WORKSPACE_SEED_ANALYSES nunca é vazio; ?? null é defensivo */
    this.selected = this.analyses[0]?.id ?? null;
    this.persistAndNotify();
  }

  restoreReset(): void {
    if (!this.preReset) return;
    this.analyses = this.preReset.analyses;
    this.selected = this.preReset.selected;
    this.preReset = null;
    this.persistAndNotify();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Monta a análise nova com defaults HONESTOS (privado, 0 vistas, atividade real). */
  private buildAnalysis(input: WorkspaceCreateInput): WorkspaceAnalysis {
    const stamp = this.deps.now();
    return {
      id: `ws-${stamp}`,
      title: input.title,
      meta: `Criado agora · ${sectionLabelOf(input.targetSection)}`,
      share: 'Privado',
      created: this.formatDate(stamp),
      layers: input.layerLabel ?? '—',
      views: '0×',
      viewsCount: 0,
      tags: input.tags,
      favorite: false,
      category: 'mine',
      createdAtMs: stamp,
      targetSection: input.targetSection,
      zona: input.zona,
      year: input.year,
      layerLabel: input.layerLabel,
      activity: [{ actor: 'Análise criada', detail: 'por você · agora' }],
    };
  }

  /** Formata epoch → "03 jun 2026" em UTC (determinístico com relógio injetado). */
  private formatDate(ms: number): string {
    const date = new Date(ms);
    const day = String(date.getUTCDate()).padStart(2, '0');
    /* v8 ignore next -- getUTCMonth() ∈ [0,11]; o ?? só existe p/ noUncheckedIndexedAccess */
    const month = MONTH_ABBR_PT[date.getUTCMonth()] ?? 'jan';
    return `${day} ${month} ${date.getUTCFullYear()}`;
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }

  private persistAndNotify(): void {
    this.persist(this.analyses);
    this.notify();
  }

  /** Grava {version,analyses}; ignora falha de storage (best-effort). */
  private persist(analyses: readonly WorkspaceAnalysis[]): void {
    const payload: WorkspacePersistedState = { version: WORKSPACE_SCHEMA_VERSION, analyses };
    try {
      this.deps.storage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Provenance: localStorage pode lançar (modo privado/quota); persistência é
      //   best-effort — nunca quebra a UI (igual collapse-controller.ts).
    }
  }

  /** Carrega do storage se válido; senão repovoa e grava a semente. */
  private loadOrSeed(): WorkspaceAnalysis[] {
    const stored = this.readStored();
    if (stored) return stored;
    const seeded = [...WORKSPACE_SEED_ANALYSES];
    this.persist(seeded);
    return seeded;
  }

  /** Lê e valida o payload persistido; null em qualquer falha (→ reseed). */
  private readStored(): WorkspaceAnalysis[] | null {
    const raw = this.safeGet();
    if (!raw) return null;
    const parsed = this.safeParse(raw);
    if (!isPersistedState(parsed)) return null;
    if (parsed.analyses.length === 0) return null;
    return [...parsed.analyses];
  }

  /** getItem tolerante a storage que lança (modo privado). */
  private safeGet(): string | null {
    try {
      return this.deps.storage.getItem(WORKSPACE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** JSON.parse tolerante a conteúdo inválido. */
  private safeParse(raw: string): unknown {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
}

/** Fábrica do store (DI de relógio + storage). */
export function createWorkspaceStore(deps: WorkspaceStoreDeps): WorkspaceStore {
  return new WorkspaceAnalysisStore(deps);
}
